import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { leadSourcesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  FunnelSimple,
  ArrowsDownUp,
} from '@phosphor-icons/react';

export default function LeadSourceMasterPage() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await leadSourcesAPI.list();
      setSources(data.lead_sources || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load lead sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingSource(null);
    setFormName('');
    setFormDesc('');
    setFormActive(true);
    setShowDialog(true);
  };

  const openEdit = (source) => {
    setEditingSource(source);
    setFormName(source.name);
    setFormDesc(source.description || '');
    setFormActive(source.is_active !== false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingSource) {
        await leadSourcesAPI.update(editingSource.id, {
          name: formName.trim(),
          description: formDesc,
          is_active: formActive,
        });
        toast.success('Lead source updated');
      } else {
        await leadSourcesAPI.create({
          name: formName.trim(),
          description: formDesc,
          is_active: formActive,
        });
        toast.success('Lead source created');
      }
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await leadSourcesAPI.delete(id);
      toast.success('Lead source deleted');
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSeed = async () => {
    try {
      const { data } = await leadSourcesAPI.seed();
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error('Failed to seed lead sources');
    }
  };

  const filtered = sources.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageHeader
        title="Lead Source Master"
        subtitle="Manage lead sources for sales orders and invoices"
      />

      <div className="mt-6 space-y-4">
        {/* Actions Bar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search lead sources..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="lead-source-search"
            />
          </div>
          <Button onClick={openCreate} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-lead-source-btn">
            <Plus size={16} className="mr-1.5" /> Add Lead Source
          </Button>
          {sources.length === 0 && (
            <Button variant="outline" onClick={handleSeed} data-testid="seed-lead-sources-btn">
              <ArrowsDownUp size={16} className="mr-1.5" /> Auto-Populate from Data
            </Button>
          )}
        </div>

        <p className="text-sm text-[#64748B]">{filtered.length} lead source{filtered.length !== 1 ? 's' : ''}</p>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="lead-sources-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Description</th>
                <th className="text-center p-3">Status</th>
                <th className="text-left p-3">Created</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((source) => (
                  <tr
                    key={source.id}
                    className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                    data-testid={`lead-source-row-${source.id}`}
                  >
                    <td className="p-3 font-medium text-[#0F172A]">{source.name}</td>
                    <td className="p-3 text-[#64748B] max-w-[250px]">
                      <div className="truncate">{source.description || '-'}</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${
                        source.is_active !== false
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {source.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3 text-[#64748B] text-xs">
                      {source.created_at ? new Date(source.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(source)}
                          data-testid={`edit-lead-source-${source.id}`}
                        >
                          <PencilSimple size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteConfirm(source)}
                          data-testid={`delete-lead-source-${source.id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-[#64748B]">
                    {search ? 'No matching lead sources' : 'No lead sources yet. Click "Add Lead Source" or "Auto-Populate from Data" to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="lead-source-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">
              {editingSource ? 'Edit Lead Source' : 'Add Lead Source'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Name *</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Doctor Referral"
                data-testid="lead-source-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Description</label>
              <Input
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Optional description"
                data-testid="lead-source-desc-input"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formActive}
                onChange={(e) => setFormActive(e.target.checked)}
                className="rounded border-gray-300"
                data-testid="lead-source-active-toggle"
              />
              <span className="text-sm text-[#0F172A]">Active</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]"
              data-testid="lead-source-save-btn"
            >
              {saving ? 'Saving...' : editingSource ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Delete Lead Source</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B] py-2">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm.id)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
